<template>
  <div>
    <warning-no-match-found
    v-if="showNoMatchFound"
    :stateId="id"
    />
    <warning-match-found
    v-if="showMatchFound"
    :stateId="id"
    />
    <es-loader v-if="loading" style="margin: 6.5rem"/>
    <es-exception v-if="!loading && loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-if="!loading && !loadingError">
      <div v-if="location" class="document-container"
        :class="{'edit-mode':isEditGeneral}"
      >
        <div class="document">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">General</div>
                <div class="header-subtitle">
                  View/edit general profile information.
                </div>
              </template>
              <template #actions>
                <ul>
                  <li class="no-border">
                    <div class="list-content">
                      <select class="control is-static"
                          v-model="generalView"
                          :disabled="isEditGeneral"
                          @change="switchView()">
                        <option v-for="(option, index) in generalViewOption"
                            :key="index"
                            :value="option">
                          {{ option }}
                        </option>
                      </select>
                    </div>
                  </li>
                  <li class="p-narrow no-border" v-if="hasModule('LOCID:LOC:EDIT')">
                    <div class="list-content">
                      <a :class="{disabled: currentEditPanel != '' && currentEditPanel != 'general'}"
                        @click="editPanel('general')"
                        v-if="!isEditGeneral"
                      >
                        <font-awesome-icon :icon="['fas', 'edit']" size="lg"/>
                      </a>
                    </div>
                  </li>
                </ul>
              </template>
            </es-header>
          </div>
          <div class="content">
            <es-form>
              <template #es-form-header>
                <div class="header-container">
                  <div class="header-elements">
                    <font-awesome-icon :icon="['fas', 'map-marker-alt']" size="lg" />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Location Details
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div class="fields-container">
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Name</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input"
                          :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorName,
                          'is-readonly' : editingMaster
                          }"
                          type="text"
                          maxlength="250"
                          v-model="location.name"
                          :readonly="!isEditGeneral || editingMaster"
                          @keyup.prevent="checkEmpty('name')">
                          <p v-if="hasErrorName" class="help is-danger">{{ msgName }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">State Location ID</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input is-static"
                          :class="{'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.stateLocationId"
                          readonly>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Source</label>
                    </div>
                    <div v-if="location.source !== undefined" class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-readonly' : editingMaster, 'is-danger' : hasErrorSourceCode }"
                            type="text"
                            v-model="location.source.description"
                            :readonly="!isEditGeneral || editingMaster"
                            v-if="!isEditGeneral || editingMaster"/>
                          <div v-if="editingOrganization"
                            :class="{ 'is-danger' : hasErrorSourceCode }"
                            class="select is-half-width display-block">
                            <select class="input is-full-width"
                              v-model="location.source.code">
                              <option v-for="source in sources"
                                :value="source.code"
                                v-bind:key="source.code">
                                {{ source.description }}
                              </option>
                            </select>
                          </div>
                          <p v-if="hasErrorSourceCode" class="help is-danger">
                            {{ msgSourceCode }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="generalView != 'Master'" class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Local Code
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorLocalCode }"
                          type="text"
                          v-model="location.localCode"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorLocalCode"
                            class="help is-danger">{{ msgLocalCode }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Address 1
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorStreet1,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.street1"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorStreet1" class="help is-danger">{{ msgStreet1 }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Address 2
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorStreet2,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.street2"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorStreet2" class="help is-danger">{{ msgStreet2 }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">City
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorCity,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.city"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorCity" class="help is-danger">{{ msgCity }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">State
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div v-if="location.state !== undefined" class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                            'is-readonly' : editingMaster}"
                            type="text"
                            :value="location.state.description"
                            :readonly="!isEditGeneral || editingMaster"
                            v-if="!isEditGeneral || editingMaster"/>
                          <div v-if="editingOrganization"
                            class="select is-half-width">
                            <select class="input is-full-width"
                                v-model="location.state.code">
                              <option value=""></option>
                              <option v-for="state in activeStates"
                                :value="state.code"
                                v-bind:key="state.code">
                                {{ state.description }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Zip
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input"
                              :class="{'is-danger' : hasErrorZip}"
                              type="text"
                              v-model.lazy="location.zip"
                              @change="checkFormat('zip')"
                              v-if="isEditGeneral && !editingMaster"
                              :disabled="editingMaster">
                          <input class="input"
                            :class="{'is-static': !isEditGeneral,
                            'is-readonly' : editingMaster}"
                            type="text"
                            :value="maskZip(location.zip)"
                            readonly
                            v-else>
                          <p v-if="hasErrorZip" class="help is-danger">{{ msgZip }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
            <es-form>
              <template #es-form-header>
                <div class="header-container">
                  <div class="header-elements">
                    <font-awesome-icon :icon="['fas', 'address-book']" size="lg"/>
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Contact
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div class="fields-container">
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">First Name
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorAdminFirstName,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.adminFirstName"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorAdminFirstName"
                            class="help is-danger">{{ msgAdminFirstName }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Last Name
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorAdminLastName,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.adminLastName"
                          :readonly="!isEditGeneral || editingMaster">
                          <p v-if="hasErrorAdminLastName"
                            class="help is-danger">{{ msgAdminLastName }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Title
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div v-if="location.title !== undefined" class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                            'is-readonly' : editingMaster}"
                            type="text"
                            v-model="location.title.description"
                            :readonly="!isEditGeneral || editingMaster"
                            v-if="!isEditGeneral || editingMaster"/>
                          <div v-if="editingOrganization"
                            class="select is-half-width">
                            <select class="input is-full-width"
                              v-model="location.title.code">
                              <option value=""></option>
                              <option v-for="title in activeTitles"
                                :value="title.code"
                                v-bind:key="title.code">
                                {{ title.description }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Email
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input" :class="{ 'is-static': !isEditGeneral,
                          'is-danger' : hasErrorAdminEmail,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model="location.adminEmail"
                          :readonly="!isEditGeneral || editingMaster"
                          @change="checkFormat('adminEmail')">
                          <p v-if="hasErrorAdminEmail" class="help is-danger">{{ msgAdminEmail }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Phone
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input class="input"
                          :class="{'is-danger' : hasErrorAdminPhone,
                          'is-readonly' : editingMaster}"
                          type="text"
                          v-model.lazy="location.adminPhone"
                          @change="checkFormat('adminPhone')"
                          v-if="editingOrganization">
                          <input class="input is-static"
                          :class="{'is-readonly' : editingMaster}"
                          type="text"
                          :value="maskPhone(location.adminPhone)"
                          readonly
                          v-else>
                          <p v-if="hasErrorAdminPhone" class="help is-danger">{{ msgAdminPhone }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>

            <es-form>
              <template #es-form-header>
                <div class="header-container">
                  <div class="header-elements">
                    <font-awesome-icon :icon="['fas', 'map-marked-alt']" size="lg"/>
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      More
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div class="fields-container">
                  <div v-if="generalView === 'Master'" class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Status</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control" v-if="!isEditGeneral">
                          <input
                          v-if="location.active"
                          class="input"
                          type="text"
                          :class="{ 'is-static': !isEditGeneral}"
                          :readonly="!isEditGeneral"
                          value="Active">
                          <input
                          v-else
                          class="input"
                          type="text"
                          :class="{ 'is-static': !isEditGeneral}"
                          :readonly="!isEditGeneral"
                          value="Inactive">
                        </div>
                        <div v-else class="control">
                          <input class="is-checkradio"
                          id="locationActiveTrue"
                          type="radio"
                          v-model="location.active"
                          :value="true">
                        <label class="radio"
                          for="locationActiveTrue">
                          Active
                        </label>
                        <input class="is-checkradio"
                          id="locationActiveFalse"
                          type="radio"
                          v-model="location.active"
                          :value="false">
                        <label class="radio"
                          for="locationActiveFalse">
                          Inactive
                        </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Start Date</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                            <input type="text"
                              class="input"
                              :class="{ 'is-static': !isEditGeneral }"
                              readonly
                              :value="location.startDateYYYYMMDD | formatDateMMDDYYYY"
                              v-if="!isEditGeneral"/>
                            <input
                              class="input calendar is-half-width"
                              :class="{ 'is-danger' : hasErrorStartDate }"
                              type="date"
                              v-model="location.startDateYYYYMMDD"
                              v-if="isEditGeneral"
                              @change.prevent="checkFormat('startDate')"/>
                        </div>
                        <span v-if="hasErrorStartDate" class="help is-danger">
                         {{ msgStartDate }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">End Date
                        <span class="text-gray-light" v-if= "isEditGeneral"> (optional)</span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                            <input type="text"
                              class="input"
                              :class="{ 'is-static': !isEditGeneral }"
                              readonly
                              :value="location.endDateYYYYMMDD | formatDateMMDDYYYY"
                              v-if="!isEditGeneral"/>
                            <input
                              class="input calendar is-half-width"
                              :class="{ 'is-danger' : hasErrorEndDate }"
                              type="date"
                              v-model="location.endDateYYYYMMDD"
                              v-if="isEditGeneral"
                              @change.prevent="checkFormat('endDate')"/>
                        </div>
                        <span v-if="hasErrorEndDate" class="help is-danger">
                         {{ msgEndDate }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Last Updated</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                            <input type="text"
                              class="input is-static"
                              :class="{'is-readonly' : isEditGeneral}"
                              readonly
                              :value="location.lastUpdateDate | formatDateMMDDYYYY"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Last Updated By</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input type="text"
                            class="input is-static"
                            :class="{'is-readonly' : isEditGeneral}"
                            readonly
                            :value="location.lastUpdateBy"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Created</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input type="text"
                            class="input is-static"
                            :class="{'is-readonly' : isEditGeneral}"
                            readonly
                            :value="location.createdDate | formatDateMMDDYYYY"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Created By</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input type="text"
                            class="input is-static"
                            :class="{'is-readonly' : isEditGeneral}"
                            readonly
                            :value="location.createdBy"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
          </div>
          <div class="es-actions">
            <es-form-actions v-if="isEditGeneral">
              <template #form-actions>
                <div class="bottom-content">
                  <ul>
                    <li>
                      <div class="list-content">
                        <a @click.prevent="clearAll('general')">Cancel</a>
                      </div>
                    </li>
                    <li>
                      <div class="list-content">
                        <button
                            class="button is-primary"
                            @click.prevent="verifyForm('general')">
                             Update
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
      <div v-if="this.locationGrades" class="document-container"
      :class="{'edit-mode':isEditGrade}">
        <div class="document">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">Grade</div>
                <div class="header-subtitle">
                  View/edit grade for this location.
                </div>
              </template>
              <template #actions v-if="hasModule('LOCID:LOC:EDIT')">
                <ul>
                  <li class="no-border">
                    <div class="list-content">
                      <a :class="{disabled: currentEditPanel != '' && currentEditPanel != 'grade'}"
                        @click="editPanel('grade')"
                        v-if="!isEditGrade">
                        <font-awesome-icon :icon="['fas', 'edit']" size="lg"/>
                      </a>
                    </div>
                  </li>
                </ul>
              </template>
            </es-header>
          </div>
          <form-location-mapping maptype = 'Grade'
            :mappings = 'locationGrades'
            :lookups= 'activeGrades'
            :editmode = 'isEditGrade'
            :hasErrorCode = 'hasErrorCode'
            @setHasErrorCode="setHasErrorCode"/>
          <div class="es-actions">
            <es-form-actions v-if="isEditGrade">
              <template #form-actions>
                <div class="bottom-content">
                  <ul>
                    <li>
                      <div class="list-content">
                        <a @click.prevent="clearAll('grade')">Cancel</a>
                      </div>
                    </li>
                    <li>
                      <div class="list-content">
                        <button
                            class="button is-primary"
                            @click.prevent="verifyForm('grade')">
                             Update
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
      <div v-if="this.locationFacilities" class="document-container"
      :class="{'edit-mode':isEditFacility}">
        <div class="document">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">Facility Type</div>
                <div class="header-subtitle">
                  View/edit facility type for this location.
                </div>
              </template>
              <template #actions v-if="hasModule('LOCID:LOC:EDIT')">
                <ul>
                  <li class="no-border">
                    <div class="list-content">
                      <a :class="{disabled: currentEditPanel != '' && currentEditPanel != 'facility'}"
                        @click="editPanel('facility')"
                        v-if="!isEditFacility">
                        <font-awesome-icon :icon="['fas', 'edit']" size="lg"/>
                      </a>
                    </div>
                  </li>
                </ul>
              </template>
            </es-header>
          </div>
          <form-location-mapping maptype = 'Facility Type'
            :mappings = 'locationFacilities'
            :lookups= 'activeFacilities'
            :editmode = 'isEditFacility'
            :hasErrorCode = 'hasErrorCode'
            @setHasErrorCode="setHasErrorCode"/>
          <div class="es-actions">
            <es-form-actions v-if="isEditFacility">
              <template #form-actions>
                <div class="bottom-content">
                  <ul>
                    <li>
                      <div class="list-content">
                        <a @click.prevent="clearAll('facility')">Cancel</a>
                      </div>
                    </li>
                    <li>
                      <div class="list-content">
                        <button
                            class="button is-primary"
                            @click.prevent="verifyForm('facility')">
                             Update
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
      <div v-if="this.locationAttributes" class="document-container"
      :class="{'edit-mode':isEditAttribute}">
        <div class="document">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">Attribute</div>
                <div class="header-subtitle">
                  View/edit attribute for this location.
                </div>
              </template>
              <template #actions v-if="hasModule('LOCID:LOC:EDIT')">
                <ul>
                  <li class="no-border">
                    <div class="list-content">
                      <a :class="{disabled: currentEditPanel != ''
                        && currentEditPanel != 'attribute'}"
                        @click="editPanel('attribute')"
                        v-if="!isEditAttribute">
                        <font-awesome-icon :icon="['fas', 'edit']" size="lg"/>
                      </a>
                    </div>
                  </li>
                </ul>
              </template>
            </es-header>
          </div>
          <form-location-mapping maptype = 'Attribute'
            :mappings = 'locationAttributes'
            :lookups= 'activeAttributes'
            :editmode = 'isEditAttribute'
            :hasErrorCode = 'hasErrorCode'
            @setHasErrorCode="setHasErrorCode"/>
          <div class="es-actions">
            <es-form-actions v-if="isEditAttribute">
              <template #form-actions>
                <div class="bottom-content">
                  <ul>
                    <li>
                      <div class="list-content">
                        <a @click.prevent="clearAll('attribute')">Cancel</a>
                      </div>
                    </li>
                    <li>
                      <div class="list-content">
                        <button
                            class="button is-primary"
                            @click.prevent="verifyForm('attribute')">
                             Update
                        </button>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
      <div v-if="this.locationGroups" class="document-container">
        <div class="document">
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">Location Group</div>
                <div class="header-subtitle">
                  View associated groups for this location.
                </div>
              </template>
            </es-header>
          </div>
          <div class="content">
            <es-form>
              <template #es-form>
                <table class="is-full-width">
                  <thead>
                    <tr>
                      <th width="50%"></th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody v-if="locationGroups.length">
                    <tr
                      v-for="(mapping, index) in locationGroups"
                      :key="index">
                      <td>
                        <div class="badge badge-secondary">
                          <span>{{mapping.locationGroup.code}} - </span>
                          <span>
                            <router-link
                              :to="{
                                name: 'groupProfileDetails',
                                params: { groupId: mapping.locationGroupId }
                              }">
                              {{mapping.locationGroup.label}}
                            </router-link>
                          </span>
                          <span class="tag">{{mapping.locationGroup.organization.abbreviation}}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          {{ mapping.startDate | formatDateMMDDYYYY }}
                        </div>
                      </td>
                      <td>
                        <div>
                          {{ mapping.endDate | formatDateMMDDYYYY }}
                        </div>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7" class="has-text-centered">
                        <div class="no-result-container">
                          <div class="icon">
                            <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="3x"/>
                          </div>
                          <p></p>
                          <h5>No results were found.</h5>
                          <p>Please go to <router-link :to="{ name: 'groupList' }">Location Group</router-link> page to add this location to a group</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </es-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAddressBook,
  faAngleDoubleRight,
  faAngleRight,
  faClone,
  faTimes,
  faCheckCircle,
  faEdit,
  faEllipsisH,
  faFilter,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsFormActions from '@/components/EsFormActions.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import PhoneUtils from '@/mixins/PhoneUtils';
import EmailUtils from '@/mixins/EmailUtils';
import DateUtils from '@/mixins/DateUtils';
import FormLocationMapping from '@/components/FormLocationMapping.vue';
import WarningMatchFound from '@/components/WarningMatchFound.vue';
import WarningNoMatchFound from '@/components/WarningNoMatchFound.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import ZipUtils from '@/mixins/ZipUtils';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faAddressBook,
  faAngleDoubleRight,
  faAngleRight,
  faClone,
  faTimes,
  faCheckCircle,
  faEdit,
  faEllipsisH,
  faFilter,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faExclamationTriangle,
  faSpinner,
);

export default {
  mixins: [
    PhoneUtils,
    EmailUtils,
    DateUtils,
    NotificationManager,
    PermissionManager,
    ZipUtils,
    FieldLabels,
  ],
  components: {
    EsException,
    EsHeader,
    EsForm,
    EsFormActions,
    EsLoader,
    FormLocationMapping,
    WarningMatchFound,
    WarningNoMatchFound,
  },
  props: {
    transStatus: {
      type: String,
    },
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isEditGeneral: false,
      isEditGrade: false,
      isEditFacility: false,
      isEditAttribute: false,
      currentEditPanel: '',

      startDateYYYYMMDD: '2019-12-10',
      endDateYYYYMMDD: '2019-12-10',
      lastUpdatedYYYYMMDD: '2019-12-10',
      createdYYYYMMDD: '2019-12-10',

      loading: null,
      location: {},
      locationBeforeEdit: null,
      messages: [],
      loadingError: null,

      hasErrorName: false,
      hasErrorSourceCode: false,
      hasErrorLocalCode: false,
      hasErrorStreet1: false,
      hasErrorStreet2: false,
      hasErrorCity: false,
      hasErrorZip: false,
      hasErrorAdminFirstName: false,
      hasErrorAdminLastName: false,
      hasErrorAdminEmail: false,
      hasErrorAdminPhone: false,
      hasErrorStartDate: false,
      hasErrorEndDate: false,
      hasErrorCode: false,

      msgName: '',
      msgSourceCode: '',
      msgLocalCode: '',
      msgStreet1: '',
      msgStreet2: '',
      msgCity: '',
      msgZip: '',
      msgAdminFirstName: '',
      msgAdminLastName: '',
      msgAdminEmail: '',
      msgAdminPhone: '',
      msgStartDate: '',
      msgEndDate: '',

      generalView: 'Master',
      generalViewOption: ['Master'],
      generalProfiles: {},

      apiMsg: '',
      formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',

      locationGrades: {},
      locationGradesBeforeEdit: null,

      locationFacilities: {},
      locationFacilitiesBeforeEdit: null,

      locationAttributes: {},
      locationAttributesBeforeEdit: null,

      locationGroups: {},
    };
  },
  mounted() {
    this.onLoad();
  },
  methods: {
    ...mapActions([
      'getlocationGroupMapping',
      'updateLocationOrganization',
      'updateLocationGrades',
      'updateLocationFacilities',
      'updateLocationAttributes',
      'updateLocation',
    ]),
    ...mapActions('lookups', [
      'loadStates',
      'loadTitles',
      'loadSources',
      'loadGrades',
      'loadFacilities',
      'loadAttributes',
    ]),
    async verifyForm(panel) {
      this.resetErrors();
      if (panel === 'general') {
        const changes = this.checkGeneralFormChanges();
        if (!changes) {
          this.editPanel('');
          return;
        }
        if (this.checkGeneralForm()) return;
        const locationOrganization = this.prepForGeneralRequest();
        try {
          if (this.generalView === 'Master') {
            const response = await this.updateLocation({
              location: locationOrganization,
            });
            this.loadLocationProfile(response.data.stateLocationId);
            this.messages.push('The location has been updated successfully.');
            this.hasSuccess = true;
          } else {
            const response = await this.updateLocationOrganization({
              locationOrganization,
              panel,
            })
            this.loadLocationProfile(response.data.locationOrganization.stateLocationId);
            this.messages.push('The organization location has been updated successfully.');
            this.messages.push(response.data.status);
            this.hasSuccess = true;
          }
          this.editPanel('');
          this.refreshHeader();
          this.showMessage();
        } catch (error) {
          if (error.response.status === 500) {
            this.apiMsg = 'Network Error';
          } else if (error.response.status === 400) {
            this.apiMsg = this.formErrorMsg;
            this.parseBadRequestErrors(error.response.data.message);
          } else {
            this.apiMsg = `${error.response.data.message}`;
          }
          this.messages.push(this.apiMsg);
          this.hasError = true;
          this.showMessage();
        }
      }
      if (panel === 'grade') {
        if (this.checkMappingForm('grade')) return;
        const datalocationGrades = this.prepForRequest('grade', this.locationGrades);
        try {
          const response = await this.updateLocationGrades({
            stateLocationId: this.location.stateLocationId,
            locationGrades: datalocationGrades,
          });
          this.locationGradesBeforeEdit = this.prepForDisplay('grade', response.data);
          this.locationGrades = JSON.parse(JSON.stringify(this.locationGradesBeforeEdit));
          this.messages.push('The grades have been updated successfully.');
          this.hasSuccess = true;
          this.editPanel('');
          this.showMessage();
        } catch (error) {
          if (error.response.status === 500) {
            this.apiMsg = 'Network Error';
          } else if (error.response.status === 400) {
            this.apiMsg = this.formErrorMsg;
            this.checkMappingForm('grade');
          } else {
            this.apiMsg = `${error.response.data.message}`;
          }
          this.messages.push(this.apiMsg);
          this.hasError = true;
          this.showMessage();
        }
      }
      if (panel === 'facility') {
        if (this.checkMappingForm('facility')) return;
        const datalocationFacilities = this.prepForRequest('facility', this.locationFacilities);
        try {
          const response = await this.updateLocationFacilities({
            stateLocationId: this.location.stateLocationId,
            locationFacilities: datalocationFacilities,
          })
          this.locationFacilitiesBeforeEdit = this.prepForDisplay('facility', response.data);
          this.locationFacilities = JSON.parse(JSON.stringify(this.locationFacilitiesBeforeEdit));
          this.messages.push('The Facility Type(s) have been updated successfully.');
          this.hasSuccess = true;
          this.editPanel('');
          this.showMessage();
        } catch (error) {
          if (error.response.status === 500) {
            this.apiMsg = 'Network Error';
          } else if (error.response.status === 400) {
            this.apiMsg = this.formErrorMsg;
            this.checkMappingForm('facility');
          } else {
            this.apiMsg = `${error.response.data.message}`;
          }
          this.messages.push(this.apiMsg);
          this.hasError = true;
          this.showMessage();
        }
      }
      if (panel === 'attribute') {
        if (this.checkMappingForm('attribute')) return;
        const datalocationAttributes = this.prepForRequest('attribute', this.locationAttributes);
        try {
          const response = await this.updateLocationAttributes({
            stateLocationId: this.location.stateLocationId,
            locationAttributes: datalocationAttributes,
          });
          this.locationAttributesBeforeEdit = this.prepForDisplay('attribute', response.data);
          this.locationAttributes = JSON.parse(JSON.stringify(this.locationAttributesBeforeEdit));

          this.messages.push('The Attributes have been updated successfully.');
          this.hasSuccess = true;
          this.editPanel('');
          this.showMessage();
        } catch (error) {
          if (error.response.status === 500) {
            this.apiMsg = 'Network Error';
          } else if (error.response.status === 400) {
            this.apiMsg = this.formErrorMsg;
            this.checkMappingForm('attribute');
          } else {
            this.apiMsg = `${error.response.data.message}`;
          }
          this.messages.push(this.apiMsg);
          this.hasError = true;
          this.showMessage();
        }
      }
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        const response = await Promise.all([
          this.getlocationGroupMapping(this.id),
          this.loadLocationProfile(this.id),
          this.loadFacilities(),
          this.loadAttributes(),
          this.loadStates(),
          this.loadTitles(),
          this.loadSources(),
          this.loadGrades(),
        ]);
        this.locationGroups = response[0].data;
        // eleminate dupes
        if (this.locationGroups.length > 1) {
          this.locationGroups = this.locationGroups
            .filter((v, i, a) => a.findIndex(t => (t.locationGroup.code === v.locationGroup.code)) === i);
        }
        this.initialize(response[1].data);
      } catch (error) {
        this.apiMsg = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.loadingError = true;
        this.hasError = true;
        this.showMessage();
      }
      this.resetErrors();
      this.loading = false;
    },
    loadLocationProfile(id) {
      return axios.get(`/locations/${id}/profile`);
    },
    initialize(data) {
      this.generalViewOption = ['Master'];
      if (data.locationOrganizations) {
        const abbreviations = data.locationOrganizations
          .filter(org => org.organization)
          .map(org => org.organization.abbreviation);
        this.generalViewOption.push(...abbreviations);
      }
      this.generalViewOption.forEach((item) => {
        let profile = {};
        // alert(item);
        if (item === 'Master') {
          profile = this.getProfile(data);
        } else {
          const org = data.locationOrganizations
            .filter(lo => lo.organization.abbreviation === item);
          profile = this.getProfile(org[0]);
          profile.localCode = org[0].localCode;
        }
        // common properties
        profile.stateLocationId = data.stateLocationId;
        profile.name = data.name;
        this.generalProfiles[item] = profile;
        // alert(profile.name);
      });
      this.switchView();

      this.locationGradesBeforeEdit = this.prepForDisplay('grade', data.locationGrades.filter(lg=> lg.grade.active === true));
      this.locationGrades = JSON.parse(JSON.stringify(this.locationGradesBeforeEdit));

      this.locationFacilitiesBeforeEdit = this.prepForDisplay('facility', data.locationFacilities.filter(lg=> lg.facility.active === true));
      this.locationFacilities = JSON.parse(JSON.stringify(this.locationFacilitiesBeforeEdit));

      this.locationAttributesBeforeEdit = this.prepForDisplay('attribute', data.locationAttributes.filter(lg=> lg.attribute.active === true));
      this.locationAttributes = JSON.parse(JSON.stringify(this.locationAttributesBeforeEdit));
    },
    prepForDisplay(type, arrFromDb) {
      if (arrFromDb.length === 0) return arrFromDb;
      const arrForDisplay = arrFromDb.map((item) => {

        //create a new object from the result coming from the backend but omit the "error" property.  
        // We cannot pass the result object to the backend if it is edited again since 
        // the backend API will not accept the "error" property on the result object
        const { error, ...newItem } = item;
        
        let lookup = null;
        if (type === 'grade') {
          newItem.code = item.gradeCode;
          lookup = this.activeGrades.find(l => l.code === item.gradeCode);
          if (lookup) {
            newItem.grade = lookup;
          }
        } else if (type === 'facility') {
          newItem.code = item.facilityCode;
          lookup = this.activeFacilities.find(l => l.code === item.facilityCode);
          if (lookup) {
            newItem.facility = lookup;
          }
        } else if (type === 'attribute') {
          newItem.code = item.attributeCode;
          lookup = this.activeAttributes.find(l => l.code.toString() === item.attributeCode);
          if (lookup) {
          newItem.attribute = lookup;
          }
        }
        if (lookup) newItem.description = lookup.description;
        newItem.startDateYYYYMMDD = moment(item.startDate).format('YYYY-MM-DD');
        if (newItem.endDate) {
          newItem.endDateYYYYMMDD = moment(item.endDate).format('YYYY-MM-DD');
        } else {
          newItem.endDateYYYYMMDD = '';
        }
        newItem.new = false;
        newItem.hasErrorStartDate = false;
        newItem.hasErrorEndDate = false;
        newItem.msgStartDate = '';
        newItem.msgEndDate = '';
        return newItem;
      });
      return arrForDisplay;
    },
    prepForRequest(type, arrFromDisplay) {
      const arr = arrFromDisplay.map((item) => {
        const newItem = JSON.parse(JSON.stringify(item));
        if (type === 'grade') {
          newItem.gradeCode = item.code;
        } else if (type === 'facility') {
          newItem.facilityCode = item.code;
        } else if (type === 'attribute') {
          newItem.attributeCode = item.code;
        }
        newItem.stateLocationId = this.location.stateLocationId;
        if (newItem.startDateYYYYMMDD === '') {
          delete newItem.startDate;
        } else {
          newItem.startDate = newItem.startDateYYYYMMDD;
        }
        delete newItem.startDateYYYYMMDD;
        if (newItem.endDateYYYYMMDD === '') {
          delete newItem.endDate;
        } else {
          newItem.endDate = newItem.endDateYYYYMMDD
        }
        delete newItem.endDateYYYYMMDD;
        delete newItem.new;
        delete newItem.hasErrorStartDate;
        delete newItem.msgStartDate;
        delete newItem.hasErrorEndDate;
        delete newItem.msgEndDate;
        delete newItem.code;
        delete newItem.description;
        return newItem;
      });
      return arr;
    },
    getLookupFromCode(list, code) {
      let o = { code: '', description: '' };
      if (code) {
        const found = list.filter(s => s.code === code);
        if (found) {
          if(found[0]) {
            o = { code: found[0].code, description: found[0].description };
          }
        }
      }
      return o;
    },
    getProfile(lo) {
      const p = {
        organizationCode: lo.organizationCode,
        // source: lo.source,
        name: lo.name,
        street1: lo.street1,
        street2: lo.street2,
        city: lo.city,
        zip: lo.zip,
        adminFirstName: lo.adminFirstName,
        adminLastName: lo.adminLastName,
        adminEmail: lo.adminEmail,
        adminPhone: lo.adminPhone,
        createdDate: lo.createdDate,
        createdBy: `${lo.createdByUser.firstName} ${lo.createdByUser.lastName}`,
        lastUpdateDate: lo.lastUpdateDate,
        lastUpdateBy: `${lo.lastUpdateByUser.firstName} ${lo.lastUpdateByUser.lastName}`,
      };
      if (lo.localCode !== undefined && lo.localCode !== '') {
        p.localCode = lo.localCode;
      }
      if (lo.stateCode !== undefined && lo.stateCode !== '') {
        p.state = this.getLookupFromCode(this.activeStates, lo.stateCode);
      } else {
        p.state = { code: '', description: '' };
      }
      if (lo.adminTitle !== undefined && lo.adminTitle !== '') {
        p.title = this.getLookupFromCode(this.activeTitles, lo.adminTitle);
      } else {
        p.title = { code: '', description: '' };
      }
      if (lo.startDate !== undefined && lo.startDate !== '') {
        p.startDate = lo.startDate;
        p.startDateYYYYMMDD = moment(lo.startDate).format('YYYY-MM-DD');
      } else {
        p.startDate = '';
        p.startDateYYYYMMDD = '';
      }
      if (lo.endDate) {
        p.endDate = lo.endDate;
        p.endDateYYYYMMDD = moment(lo.endDate).format('YYYY-MM-DD');
      } else {
        p.endDate = '';
        p.endDateYYYYMMDD = '';
      }
      if (lo.source !== undefined) {
        p.source = this.getLookupFromCode(this.sources, lo.source.code);
      } else {
        p.source = { code: '', description: '' };
      }
      if (lo.active !== undefined && lo.active !== '') {
        p.active = lo.active;
      } else {
        p.active = true;
      }
      return p;
    },
    prepForGeneralRequest() {
      const lo = {
        name: this.location.name,
        localCode: this.location.localCode,
        stateLocationId: this.location.stateLocationId,
        organizationCode: this.location.organizationCode,
        street1: this.location.street1,
        street2: this.location.street2,
        city: this.location.city,
        zip: this.location.zip,
        adminFirstName: this.location.adminFirstName,
        adminLastName: this.location.adminLastName,
        adminEmail: this.location.adminEmail,
      };
      if (this.location.title.code !== '') {
        lo.adminTitle = this.location.title.code;
      }
      if (this.location.state.code !== '') {
        lo.stateCode = this.location.state.code;
      }
      if (this.location.source.code !== '') {
        lo.sourceCode = this.location.source.code;
      }
      if (this.location.startDateYYYYMMDD !== '') {
        lo.startDate = this.location.startDateYYYYMMDD;
      }
      if (this.location.endDateYYYYMMDD !== '') {
        lo.endDate = this.location.endDateYYYYMMDD;
      }
      lo.adminPhone = this.unmaskPhone(this.location.adminPhone);
      if (this.generalView === 'Master') {
        lo.active = this.location.active;
      }
      return lo;
    },
    checkGeneralForm() {
      this.resetFormErrors();
      this.checkEmpty('name');
      this.checkFormat('startDate');
      this.checkFormat('adminPhone');
      this.checkFormat('zip');
      this.compareDates();
      if (!this.isEditGeneral) {
        this.checkFormat('endDate');
        this.checkFormat('adminEmail');
      }
      const err = this.hasErrorName
        || this.hasErrorStartDate
        || this.hasErrorEndDate
        || this.hasErrorAdminPhone
        || this.hasErrorAdminEmail;
      if (err === true) {
        this.messages.push(this.formErrorMsg);
        this.hasError = true;
        this.showMessage();
      }
      return err;
    },
    checkGeneralFormChanges() {
      if (this.location.name !== this.locationBeforeEdit.name
        || this.location.localCode !== this.locationBeforeEdit.localCode
        || this.location.organizationCode !== this.locationBeforeEdit.organizationCode
        || this.location.street1 !== this.locationBeforeEdit.street1
        || this.location.street2 !== this.locationBeforeEdit.street2
        || this.location.city !== this.locationBeforeEdit.city
        || this.location.zip !== this.locationBeforeEdit.zip
        || this.location.state.code !== this.locationBeforeEdit.state.code
        || this.location.adminFirstName !== this.locationBeforeEdit.adminFirstName
        || this.location.adminLastName !== this.locationBeforeEdit.adminLastName
        || this.location.adminEmail !== this.locationBeforeEdit.adminEmail
        || this.location.adminPhone !== this.locationBeforeEdit.adminPhone
        || this.location.title.code !== this.locationBeforeEdit.title.code
        || this.location.source.code !== this.locationBeforeEdit.source.code
        || this.location.active !== this.locationBeforeEdit.active
        || this.location.startDateYYYYMMDD !== this.locationBeforeEdit.startDateYYYYMMDD
        || this.location.endDateYYYYMMDD !== this.locationBeforeEdit.endDateYYYYMMDD) {
        return true;
      }
      return false;
    },
    checkEmpty(field) {
      if (field === 'name') {
        if (this.location.name === '') {
          this.hasErrorName = true;
          this.msgName = 'Name can not be empty';
        } else {
          this.hasErrorName = false;
          this.msgName = '';
        }
      }
    },
    checkFormat(field) {
      if (field === 'startDate') {
        if (this.location.startDateYYYYMMDD === '') {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is required.';
        } else if (!this.isValidDateFormat(this.location.startDateYYYYMMDD, 'YYYY-MM-DD')) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is invalid.';
        } else {
          const startDate = this.getCorrectDateFromYYYYMMDDString(this.location.startDateYYYYMMDD)
            .getTime();
          const today = new Date().getTime();
          if (moment(startDate).isAfter(today)) {
            this.hasErrorStartDate = true;
            this.msgStartDate = 'Start Date cannot be future date.';
          } else {
            this.hasErrorStartDate = false;
            this.msgStartDate = '';
          }
        }
      }
      if (field === 'endDate') {
        if (this.location.endDateYYYYMMDD !== '') {
          if (!this.isValidDateFormat(this.location.endDateYYYYMMDD, 'YYYY-MM-DD')) {
            this.hasErrorEndDate = true;
            this.msgEndDate = 'End Date is invalid.';
          } else {
            const endDate = this.getCorrectDateFromYYYYMMDDString(this.location.startDateYYYYMMDD)
              .getTime();
            const today = new Date().getTime();
            if (moment(endDate).isAfter(today)) {
              this.hasErrorEndDate = true;
              this.msgEndDate = 'End Date cannot be future date.';
            } else {
              this.hasErrorEndDate = false;
              this.msgEndDate = '';
            }
          }
        } else {
          this.hasErrorEndDate = false;
          this.msgEndDate = '';
        }
      }
      if (field === 'adminPhone') {
        if (this.location.adminPhone === '') {
          this.hasErrorAdminPhone = false;
          this.msgAdminPhone = '';
        } else if (!this.isValidPhone(this.location.adminPhone)) {
          this.hasErrorAdminPhone = true;
          this.msgAdminPhone = 'Phone number is invalid.';
        } else {
          this.hasErrorAdminPhone = false;
          this.msgAdminPhone = '';
        }
      }
      if (field === 'adminEmail') {
        if (this.location.adminEmail !== '' && this.location.adminEmail !== undefined
          && !this.isWelformed(this.location.adminEmail)) {
          this.hasErrorAdminEmail = true;
          this.msgAdminEmail = 'Email address is invalid.';
        } else {
          this.hasErrorAdminEmail = false;
          this.msgAdminEmail = '';
        }
      }
      if (field === 'zip') {
        if (this.location.zip === '') {
          this.hasErrorZip = false;
          this.msgZip = '';
        } else if (!this.isValidZip(this.location.zip)) {
          this.hasErrorZip = true;
          this.msgZip = 'Zip is invalid.';
        } else {
          this.hasErrorZip = false;
          this.msgZip = '';
        }
      }
    },
    compareDates() {
      if (this.location.startDateYYYYMMDD !== ''
          && this.isValidDateFormat(this.location.startDateYYYYMMDD, 'YYYY-MM-DD')
          && this.location.endDateYYYYMMDD !== ''
          && this.isValidDateFormat(this.location.endDateYYYYMMDD, 'YYYY-MM-DD')) {
        const startDate = this.getCorrectDateFromYYYYMMDDString(this.location.startDateYYYYMMDD)
          .getTime();
        const endDate = this.getCorrectDateFromYYYYMMDDString(this.location.endDateYYYYMMDD)
          .getTime();
        if (moment(startDate).isAfter(endDate)) {
          this.hasErrorEndDate = true;
          this.msgEndDate = 'End Date cannot be earlier than Start Date.';
        }
      }
    },
    checkMappingForm(panel) {
      let mappings = null;
      if (panel === 'grade') {
        mappings = this.locationGrades;
      } else if (panel === 'attribute') {
        mappings = this.locationAttributes;
      } else if (panel === 'facility') {
        mappings = this.locationFacilities;
      }
      this.hasErrorCode = mappings.find(l => l.code === '');
      const err = mappings
        .some(m => m.code === '' || m.hasErrorStartDate || m.hasErrorEndDate);
      if (err) {
        this.messages.push(this.formErrorMsg);
        this.hasError = true;
        this.showMessage();
      }
      return this.hasError;
    },
    resetErrors() {
      this.messages = [];
      this.hasError = false;
      this.hasSuccess = false;
    },
    resetFormErrors() {
      this.hasErrorName = false;
      this.hasErrorStreet1 = false;
      this.hasErrorStreet2 = false;
      this.hasErrorCity = false;
      this.hasErrorZip = false;
      this.hasErrorAdminFirstName = false;
      this.hasErrorAdminLastName = false;
      this.hasErrorAdminEmail = false;
      this.hasErrorAdminPhone = false;
      this.hasErrorStartDate = false;
      this.hasErrorEndDate = false;
      this.hasErrorCode = false;
      this.hasErrorSourceCode = false
    },
    switchView() {
      const objFromArray = Object.assign({}, this.generalProfiles[this.generalView]);
      this.locationBeforeEdit = this.copy(objFromArray);
      this.location = this.copy(objFromArray);
    },
    editPanel(panel) {
      if (panel === 'general') {
        this.isEditGeneral = true;
        this.currentEditPanel = 'general';
        this.hideNotification();
      } else if (panel === 'grade') {
        this.isEditGrade = true;
        this.currentEditPanel = 'grade';
        this.hideNotification();
      } else if (panel === 'attribute') {
        this.isEditAttribute = true;
        this.currentEditPanel = 'attribute';
        this.hideNotification();
      } else if (panel === 'facility') {
        this.isEditFacility = true;
        this.currentEditPanel = 'facility';
        this.hideNotification();
      } else {
        this.isEditGeneral = false;
        this.isEditGrade = false;
        this.isEditFacility = false;
        this.isEditAttribute = false;
        this.currentEditPanel = '';
      }
    },
    undoForm(section) {
      if (section === 'general') {
        this.location = this.copy(this.locationBeforeEdit);
      }
      if (section === 'grade') {
        this.locationGrades = JSON.parse(JSON.stringify(this.locationGradesBeforeEdit));
      }
      if (section === 'facility') {
        this.locationFacilities = JSON.parse(JSON.stringify(this.locationFacilitiesBeforeEdit));
      }
      if (section === 'attribute') {
        this.locationAttributes = JSON.parse(JSON.stringify(this.locationAttributesBeforeEdit));
      }
    },
    clearAll(section) {
      this.currentEditPanel = '';
      if (section === 'general') {
        this.undoForm(section);
        this.isEditGeneral = false;
      }
      if (section === 'grade') {
        this.undoForm(section);
        this.isEditGrade = false;
      }
      if (section === 'facility') {
        this.undoForm(section);
        this.isEditFacility = false;
      }
      if (section === 'attribute') {
        this.undoForm(section);
        this.isEditAttribute = false;
      }
      this.resetFormErrors();
      this.hideNotification();
    },
    parseBadRequestErrors(data) {
      const messages = data.split(',');
      const errors = messages.map((item) => {
        const idx = item.search(/[: ]/);
        const e = idx === -1
          ? [item]
          : { field: item.slice(0, idx), error: item.slice(idx + 1).trim() };
        return e;
      });
      errors.forEach((e) => {
        switch (e.field) {
          case 'first':
            this.hasErrorAdminFirstName = true;
            this.msgAdminFirstName += e.error;
            break;
          case 'last':
            this.hasErrorAdminLastName = true;
            this.msgAdminLastName += e.error;
            break;
          case 'phone':
            this.hasErrorAdminPhone = true;
            this.msgPhone1 += e.error;
            break;
          case 'street1':
            this.hasErrorStreet1 = true;
            this.msgStreet1 += e.error;
            break;
          case 'street2':
            this.hasErrorStreet2 = true;
            this.msgStreet2 += e.error;
            break;
          case 'city':
            this.hasErrorCity = true;
            this.msgCity += e.error;
            break;
          case 'zip':
            this.hasErrorZip = true;
            this.msgZip += e.error;
            break;
          case 'sourceCode':
            this.hasErrorSourceCode = true;
            this.msgSourceCode = e.error;
            break;
          default:
        }
      });
    },
    showMessage() {
      const type = this.hasError ? 'danger' : 'success';
      this.showNotification(type, this.messages);
    },
    refreshHeader() {
      this.$emit('updated');
    },
    copy(o) { // copy object or array
      let v;
      const output = Array.isArray(o) ? [] : {};

      Object.entries(o).forEach((e) => {
        const key = e[0];
        v = o[key];
        if (v) {
          output[key] = (typeof v === 'object') ? this.copy(v) : v;
        } else {
          output[key] = v;
        }
      });
      return output;
    },
    setHasErrorCode(e) {
      this.hasErrorCode = e;
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'activeStates',
      'activeTitles',
      'sources',
      'activeGrades',
      'activeFacilities',
      'activeAttributes',
    ]),
    editingMaster() {
      return this.isEditGeneral && this.generalView === 'Master';
    },
    editingOrganization() {
      return this.isEditGeneral && this.generalView !== 'Master';
    },
    showNoMatchFound() {
      if (this.transStatus === 'LOCID_NO_MATCHES_NEW_ID_INSERTED' || this.transStatus === 'LOCID_NEAR_MATCH_RESOLVE_BY_CREATE_NEW') {
        return true;
      }
      return false;
    },
    showMatchFound() {
      if (this.transStatus === 'LOCID_MATCHED_VIA_SEARCH' || this.transStatus === 'LOCID_NEAR_MATCH_RESOLVE_BY_ASSIGN_ID') {
        return true;
      }
      return false;
    },
  },
};
</script>
<style lang="sass" scoped>
  .document
    max-width: 928px
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .is-readonly
    background: #eee
  .no-result-container
    padding: 1.0rem
</style>
